import { defineStore, acceptHMRUpdate } from 'pinia';
import getValue from 'get-value';
import setValue from 'set-value';
import { updateCustomClientAPI } from '../lib/callModContent';
import { ref, computed, nextTick } from 'vue';
import { cloneDeep, isEqual } from 'lodash-es';
import applyPatch, {
  findDotPathToIdOrThrow,
  findScreenById
} from '../lib/applyPatch';
import { resetEmptyPatchProperties } from '../lib/resetEmptyPatchProperties';
import bugAlert from '../lib/bugAlert';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getCustomContent } from '../lib/getContent';
import isEditModeAvailable from '../lib/isEditModeAvailable';
import useTitle from './useTitle';
import SCORM from '../lib/SCORM';
import getEditorWindow from '../lib/getEditorWindow';

// clone content any time we need to mutate it with set-value
// const clone = <T>(obj: T): T => clone(obj);

const defaultContent: Content = {
  pages: [],
  course: { title: '' },
  patches: {}
};

type Edits = Partial<Content>;

const defaultEdits: Edits = {
  patches: {}
};

interface State {
  content: Content;
  edits?: Edits;
}

const insertRegex = /^(<|>) *#?(\S+)/;

const useContent = defineStore('content', () => {
  const content = ref<Content>(defaultContent);
  const edits = ref<Edits>(defaultEdits);
  const translationsInvalidated = ref(false);
  const titleStore = useTitle();

  const saveStatus = ref<'clean' | 'unsaved' | 'saved' | 'saving' | 'error'>(
    'clean'
  );

  const compiled = computed(() => {
    const merged = Object.assign(
      {},
      // overwrite most sections
      content.value,
      edits.value,
      {
        // but merge these sections
        patches: {
          ...content.value.patches,
          ...edits.value.patches
        },
        hidden: {
          ...content.value.hidden,
          ...edits.value.hidden
        }
      }
    );

    const output = cloneDeep(merged);
    const { patches = {}, previews = {} } = output;

    const toApply = Object.entries({ ...patches, ...previews });

    if (toApply.length > 0) {
      let failedPatches: [string, Patch][] = [];
      toApply.forEach(patch => {
        const success = applyPatch(output)(patch);
        if (!success) {
          failedPatches.push(patch);
        }
      });

      // reattempt the failed advanced patches
      failedPatches.forEach(patch => {
        const success = applyPatch(output)(patch);
        if (!success) {
          bugAlert(
            `Failed to apply patch: ${patch[0]}\n${JSON.stringify(
              patch[1],
              null,
              2
            )}`
          );
        }
      });
    }

    // add pageIndex and screenIndex to each page and screen for consistent numbering regardless of filters during rendering
    output?.pages.forEach((p, i) => {
      p.pageIndex = i;
      p.screens.forEach((s, j) => {
        s.screenIndex = j;
        if (s.bank) s.bank.forEach((b, k) => (b.screenIndex = k));
      });
    });

    // validate for duplicate ids, deferred
    setTimeout(() => {
      const { pages, quizzes } = output;
      const duplicates = JSON.stringify({ pages, quizzes }, null, 2)
        .match(/"id": "(\S+)"/g)
        ?.map(s => s.slice(7, -1))
        ?.filter((id, i, ids) => ids.indexOf(id) !== i);

      if (duplicates?.length) {
        bugAlert(
          `The course has duplicate ids, which may corrupt user progress:\n  ${duplicates.join(
            ', '
          )}`
        );
      }
    }, 2e3);

    return output;
  });

  // Committed compiled (patches only, no uncommitted previews). Lazy computed —
  // never evaluated at runtime, only read by the author shell as the agent's
  // match/compose base. Including previews there double-applies edits on
  // recompose. Kept separate from `compiled` to leave the runtime render path
  // untouched.
  const compiledNoPreviews = computed(() => {
    const merged = Object.assign({}, content.value, edits.value, {
      patches: {
        ...content.value.patches,
        ...edits.value.patches
      },
      hidden: {
        ...content.value.hidden,
        ...edits.value.hidden
      }
    });

    const output = cloneDeep(merged);
    const { patches = {} } = output;
    const toApply = Object.entries(patches);

    if (toApply.length > 0) {
      const failedPatches: [string, Patch][] = [];
      toApply.forEach(patch => {
        if (!applyPatch(output)(patch)) failedPatches.push(patch);
      });
      // reattempt failed advanced patches; swallow failures here (the main
      // `compiled` computed owns user-facing patch-failure alerts)
      failedPatches.forEach(patch => applyPatch(output)(patch));
    }

    output?.pages.forEach((p, i) => {
      p.pageIndex = i;
      p.screens.forEach((s, j) => {
        s.screenIndex = j;
        if (s.bank) s.bank.forEach((b, k) => (b.screenIndex = k));
      });
    });

    return output;
  });

  const load = (state: State) => {
    if (state.content) {
      // Drop edits.patches that the reloaded content now owns, so a transient
      // overlay (add-page/logo seed) can't permanently shadow the fresh value.
      if (edits.value.patches && state.content.patches) {
        for (const key of Object.keys(edits.value.patches)) {
          if (key in state.content.patches) delete edits.value.patches[key];
        }
      }
      // VP-3886: also drop a whole-screen overlay that equals the reloaded
      // pages screen (image edits routed to pages) — render-neutral, and stops
      // the stale snapshot masking later draft edits to the same screen.
      if (edits.value.patches) {
        for (const [key, patch] of Object.entries(edits.value.patches)) {
          if (/^[<>$#]/.test(key)) continue;
          const pagesScreen = findScreenById(state.content, key);
          if (pagesScreen && isEqual(patch?.[0], pagesScreen)) {
            delete edits.value.patches[key];
          }
        }
      }
      content.value = state.content;
    }
    if (state.edits) edits.value = state.edits;

    // reset any empty saved patches values to the original value
    if (isEditModeAvailable && compiled.value.patches) {
      Object.entries(compiled.value.patches).forEach(
        resetEmptyPatchProperties(compiled.value, resetPath)
      );
    }

    // update title store with the current course title
    titleStore.updateTitle(compiled.value.course?.title || '');
    titleStore.updateSubtitle(compiled.value.course?.subtitle || '');

    window.dispatchEvent(new CustomEvent('uc:compiled-updated'));

    nextTick(() => ScrollTrigger.refresh());
  };

  const commit = async () => {
    // Skip saving in sales demo mode to prevent SCORM write issues
    if (SCORM.isFirm('VINCI_ADMIN')) {
      return;
    }

    // original firm content, from S3 or DB
    const custom = getCustomContent();

    const merged: Partial<Content> = Object.assign({}, custom, edits.value, {
      patches: { ...custom?.patches, ...edits.value.patches },
      hidden: { ...custom?.hidden, ...edits.value.hidden }
    });

    // remove en-GB from languages (added by the language widget)
    if (merged.languages)
      merged.languages = merged.languages?.filter(l => l !== 'en-GB');

    translationsInvalidated.value = true;
    saveStatus.value = 'saving';

    try {
      await Promise.all([
        updateCustomClientAPI(merged),
        // show the "saving" message for at least 1 second
        new Promise(resolve => setTimeout(resolve, 1e3))
      ]);

      window.dispatchEvent(new CustomEvent('uc:compiled-updated'));
      saveStatus.value = 'saved';
    } catch (e) {
      saveStatus.value = 'error';
      // bugAlert(e);
    }
  };

  const setTranslationsInvalidated = (v: boolean) => {
    translationsInvalidated.value = v;
  };

  // set a local edit value without persisting — used when persistence is
  // handled elsewhere (e.g. the authoring Monaco YAML draft), so the compiled
  // preview updates synchronously without a redundant save round-trip
  const setEditValue = (path: string, value: unknown) => {
    setValue(edits.value, path, value);
  };

  const updateValue = (path: string, value: unknown) => {
    setEditValue(path, value);
    commit();
  };

  const resetPath = (path: string): string | null => {
    if (!edits.value.patches) return null;

    if (path.startsWith('$')) {
      const originalValue = getValue(content.value, path.slice(1));

      // exit if the original value is also empty
      if (!originalValue || originalValue.length === 0) return null;

      edits.value.patches[path] = [{ value: originalValue }];
      commit();

      console.log('resetting empty string value', {
        path,
        current: edits.value.patches[path],
        original: originalValue
      });
      return originalValue;
    }

    if (path.startsWith('#')) {
      const [id, ...pathInScreen] = path.slice(1).split('.');
      const pathToId = findDotPathToIdOrThrow(content.value, id);

      const originalValue = getValue(
        content.value,
        [pathToId, ...pathInScreen].join('.')
      );

      // exit if the original value is also empty
      if (!originalValue || originalValue.length === 0) return null;

      edits.value.patches[path] = [{ value: originalValue }];
      commit();

      console.log('resetting empty string value', {
        path,
        current: edits.value.patches[path],
        original: originalValue
      });
      return originalValue;
    }

    const [[, pathToScreen, pathInScreen]] = [
      ...path.matchAll(/(pages\.\d+\.screens\.\d+)\.(\S+)/g)
    ];

    const compiledScreen = getValue(compiled.value, pathToScreen);
    const { id } = compiledScreen;

    let originalScreen = findScreenById(content.value, id);

    if (!originalScreen) return null;

    const originalValue = getValue(originalScreen, pathInScreen);

    // exit if the original value is also empty
    if (!originalValue || originalValue.length === 0) return null;

    // VP-3892: in authoring the screen's patch lives in content.patches (draft),
    // not the session edits overlay. Falling straight back to the pages copy
    // here wiped every other patched prop on the screen — check both.
    const existingPatch =
      edits.value?.patches?.[id]?.[0] ?? content.value.patches?.[id]?.[0];
    const mutatingScreen = cloneDeep(existingPatch || originalScreen);

    delete mutatingScreen?.screenIndex;
    if (mutatingScreen.bank)
      mutatingScreen.bank.forEach(b => delete b.screenIndex);

    setValue(mutatingScreen, pathInScreen, originalValue);
    edits.value.patches[id] = [mutatingScreen];
    console.log('resetting empty string value', {
      path,
      current: edits.value.patches[id],
      original: originalValue
    });

    commit();

    return originalValue;
  };

  const findAdvancedPatchScreenById = (id: string) => {
    if (!compiled.value.patches) return null;
    // iterate over the patches to see if any match an advanced patch insert '> #key' or '< #key'
    // return the patched screen if it matches the id
    for (const [k, [patch]] of Object.entries(compiled.value.patches)) {
      if (insertRegex.test(k) && patch?.id === id) {
        return { key: k, patch: patch };
      }
    }

    return null;
  };

  const setPath = (path: string, value: string) => {
    edits.value.patches = edits.value.patches || {};

    if (/^[$#]/.test(path)) {
      edits.value.patches[path] = [{ value }];

      // update title store if editing course title
      if (path === '$course.title') {
        titleStore.updateTitle(value);
      } else if (path === '$course.subtitle') {
        titleStore.updateSubtitle(value);
      }

      commit();
      return;
    }

    const [[, pathToScreen, pathInScreen]] = [
      ...path.matchAll(/(pages\.\d+\.screens\.\d+)\.(\S+)/g)
    ];

    const compiledScreen = getValue(compiled.value, pathToScreen);
    const { id, bookmark } = compiledScreen;
    const advancedPatchScreen = findAdvancedPatchScreenById(id);
    const existingPatch = advancedPatchScreen
      ? advancedPatchScreen.patch
      : (edits.value?.patches?.[id]?.[0] as UcScreen | undefined);
    const mutatingScreen = cloneDeep(existingPatch || compiledScreen);

    delete mutatingScreen?.screenIndex;
    if (mutatingScreen.bank)
      mutatingScreen.bank.forEach(b => delete b.screenIndex);

    // delete any insights-data object in the cloned screen
    /*if (mutatingScreen['insights-data']) {
      console.log('Deleted insights-data object in the cloned screen', {
        id,
        path,
        insightsData: mutatingScreen['insights-data']
      });
      delete mutatingScreen['insights-data'];
    }*/

    // delete any insights-data object in an edited assessment question
    /*if (mutatingScreen.template === 'assessment') {
      // e.g. path to the question text is assessment.questions.0.question
      // path to the first option is assessment.questions.0.options.0
      // we want the assessment.questions.0 question object
      const pathToAssessmentQuestion =
        pathInScreen.match(/(assessment\.questions\.\d+)/) || '';
      const assessmentQuestion = getValue(
        mutatingScreen,
        pathToAssessmentQuestion[0]
      );
      if (assessmentQuestion['insights-data']) {
        console.log(
          'Deleted insights-data object in the assessment questions screen',
          {
            id,
            path,
            insightsData: assessmentQuestion['insights-data']
          }
        );
        delete assessmentQuestion['insights-data'];
      }
    }*/

    // update screen.bookmark if editing a heading that matches the existing bookmark
    if (
      bookmark &&
      /(?:heading|title|subheading)$/.test(pathInScreen) &&
      bookmark.length > 0 &&
      bookmark === mutatingScreen[pathInScreen]
    ) {
      mutatingScreen.bookmark = value;
    }

    setValue(mutatingScreen, pathInScreen, value);

    // update the existing advanced patch screen if it exists, otherwise save a normal patch
    if (advancedPatchScreen) {
      edits.value.patches[advancedPatchScreen.key] = [mutatingScreen];
    } else {
      edits.value.patches[id] = [mutatingScreen];
    }
    commit();
  };

  // For now only advanced patches can be deleted
  const deleteAdvancedPatch = async (patchToDelete: string) => {
    if (!isEditModeAvailable || !patchToDelete) return;

    // check if the path is an advanced patch
    if (!/>\s[#]/.test(patchToDelete)) {
      console.error('Delete patch: path not an advanced patch', patchToDelete);
      return;
    }

    // check if the advanced patch is in edits, if not delete from content
    if (edits.value.patches && edits.value.patches[patchToDelete]) {
      const patchToDeleteChild = edits.value.patches[patchToDelete][0].id;
      const orphanedEditsPatch = `> #${patchToDeleteChild}`;
      let hasOrphanedEditsPatch = false;
      let orphanedPatchProps = null;
      // check if the deleted patch ID was used by any other advanced patches
      for (const [k, patch] of Object.entries(edits.value.patches)) {
        if (k === orphanedEditsPatch) {
          // delete the orphaned patch
          delete edits.value.patches[k];
          hasOrphanedEditsPatch = true;
          orphanedPatchProps = patch;
        }
      }
      if (hasOrphanedEditsPatch && orphanedPatchProps) {
        // update the advanced patch with orphaned content
        edits.value.patches[patchToDelete] = orphanedPatchProps;
      } else {
        // just delete the advanced patch
        delete edits.value.patches[patchToDelete];
      }
    }

    if (content.value.patches && content.value.patches[patchToDelete]) {
      const contentPatchToDeleteChild =
        content.value.patches[patchToDelete][0].id;
      const orphanedContentPatch = `> #${contentPatchToDeleteChild}`;
      let hasOrphanedContentPatch = false;
      let orphanedPatch = null;
      // check if the deleted patch ID was used by any other advanced patches
      for (const [k, patch] of Object.entries(content.value.patches)) {
        if (k === orphanedContentPatch) {
          // delete the orphaned patch
          delete content.value.patches[k];
          hasOrphanedContentPatch = true;
          orphanedPatch = patch;
        }
      }
      if (hasOrphanedContentPatch && orphanedPatch) {
        // update the advanced patch with orphaned content
        content.value.patches[patchToDelete] = orphanedPatch;
      } else {
        // just delete the advanced patch
        delete content.value.patches[patchToDelete];
      }
    }

    // In authoring, commit() doesn't reach the Monaco draft — bridge the
    // post-delete patches map in via acceptPatchesFromPreview (as add-page does).
    const hostWindow = getEditorWindow('acceptPatchesFromPreview');
    if (hostWindow?.acceptPatchesFromPreview) {
      const newPatches = { ...content.value.patches, ...edits.value.patches };
      hostWindow.acceptPatchesFromPreview(JSON.stringify(newPatches));
    } else {
      commit();
    }
  };

  return {
    content,
    edits,
    compiled,
    compiledNoPreviews,
    load,
    updateValue,
    setEditValue,
    setPath,
    resetPath,
    commit,
    saveStatus,
    translationsInvalidated,
    setTranslationsInvalidated,
    findAdvancedPatchScreenById,
    deleteAdvancedPatch
  };
});

export default useContent;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useContent, import.meta.hot));
}
